import { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { ArrowLeft, Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [sent, setSent] = useState(false);
  const [devUrl, setDevUrl] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(true);
      if (data?.dev_reset_url) setDevUrl(data.dev_reset_url);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 p-8 shadow-sm rounded-sm" data-testid="forgot-password-card">
        <Link to="/login" className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-900 text-sm mb-6 transition-colors" data-testid="forgot-back-login">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="overline text-slate-500 mb-2">Account recovery</div>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{fontFamily:"Outfit"}}>
          {sent ? "Check your inbox." : "Forgot your password?"}
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          {sent
            ? "If an account exists for that email, we've sent a reset link. It expires in 60 minutes."
            : "Enter the email associated with your RealtyVault Manager account and we'll send you a secure link to reset your password."}
        </p>

        {!sent ? (
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="overline text-slate-600">Email</label>
              <input
                type="email"
                data-testid="forgot-email-input"
                className="w-full mt-2 input-clean"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@company.com"
              />
            </div>
            {err && <div className="text-sm text-red-600" data-testid="forgot-error">{err}</div>}
            <button
              type="submit"
              disabled={busy}
              data-testid="forgot-submit-button"
              className="w-full btn-primary-dark flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {busy ? "Sending…" : (<><Mail className="w-4 h-4" /> Send reset link</>)}
            </button>
          </form>
        ) : (
          <div className="mt-8 space-y-4" data-testid="forgot-success">
            <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 p-4 rounded-sm">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
              <div className="text-sm text-emerald-900">
                We've emailed a password reset link to <strong>{email}</strong>.
              </div>
            </div>
            {devUrl && (
              <div className="bg-amber-50 border border-amber-200 p-4 rounded-sm text-xs" data-testid="forgot-dev-url">
                <div className="font-bold text-amber-900 uppercase tracking-widest mb-1">Dev link (email provider not configured)</div>
                <a href={devUrl} className="text-amber-900 underline break-all font-mono">{devUrl}</a>
              </div>
            )}
            <Link to="/login" className="inline-block text-sm text-slate-900 font-semibold hover:underline" data-testid="forgot-back-to-login">
              Return to sign in
            </Link>
          </div>
        )}

        <div className="mt-10 border-t border-slate-200 pt-6 text-xs text-slate-500">
          <div className="font-bold text-slate-900 mb-1">Forgot your username?</div>
          Your username is the email address you used when your account was created. If you can't remember it, contact your property manager or account administrator.
        </div>
      </div>
    </div>
  );
}
